import BlockOutlined from "@mui/icons-material/BlockOutlined";
import DownloadOutlined from "@mui/icons-material/DownloadOutlined";
import { Box, Button, Checkbox, type Theme, Typography } from "@mui/material";
import { type SystemStyleObject } from "@mui/system";
import { useEffect, useId, useRef } from "react";
import { MIN_TAP_TARGET } from "@/styles/responsive";
import { reassistButtonSx } from "@/features/reassistRequests/components/ReassistButton";
import {
  costItemSx,
  focusRingSx,
  NO_TRANSITION,
  rowHoverBg,
  TEXT,
} from "@/features/reassistRequests/utils/tokens";
import { SalePurchaseChip } from "./SalePurchaseChip";

interface Props {
  documentType: string;
  pageCount: number | null;
  isPurchased: boolean;
  /** 価格が未設定（購入できない）。 */
  priceUnset: boolean;
  /** 購入・ダウンロードの処理中。選択とダウンロードを止める。 */
  busy: boolean;
  /** 未購入の行が、まとめて購入する対象に選ばれているか。 */
  selected: boolean;
  onToggle: () => void;
  onDownload: () => void;
  /** 購入直後に、行が「ダウンロード」へ変わったらそのボタンへフォーカスを送る。 */
  autoFocusAction?: boolean;
  /** 一覧に選べる行があるとき、購入済みの行もチェックの幅を空けて文字の左端をそろえる。 */
  reserveCheckboxSpace?: boolean;
}

/** チェックボックスの押せる範囲（44px 四方）。見た目のアイコンは MUI の 24px のまま。 */
export const checkboxSx = (theme: Theme): SystemStyleObject<Theme> => ({
  width: MIN_TAP_TARGET,
  height: MIN_TAP_TARGET,
  p: 0,
  flexShrink: 0,
  ...focusRingSx(theme),
});

/**
 * PDF1行。未購入の行は先頭のチェックボックスで、まとめて購入する対象に選ぶ（購入は下部の購入バーから）。
 * 購入済みの行は「ダウンロード」を出す。購入前に見せてよい項目（資料の種類・ページ数）だけを扱う。
 */
export const SaleFileRow: React.FC<Props> = ({
  documentType,
  pageCount,
  isPurchased,
  priceUnset,
  busy,
  selected,
  onToggle,
  onDownload,
  autoFocusAction = false,
  reserveCheckboxSpace = true,
}) => {
  const downloadRef = useRef<HTMLButtonElement>(null);
  const checkboxId = useId();
  useEffect(() => {
    if (autoFocusAction && isPurchased) downloadRef.current?.focus();
  }, [autoFocusAction, isPurchased]);

  const typeLabel = documentType || "資料";
  const selectable = !isPurchased && !priceUnset;

  const body = (
    <Box sx={(theme) => ({ flex: `1 1 ${theme.spacing(20)}`, minWidth: 0 })}>
      <Typography
        sx={{ ...TEXT.body, fontWeight: 700, overflowWrap: "anywhere" }}
      >
        {typeLabel}
      </Typography>
      {(pageCount !== null || isPurchased) && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            columnGap: 1,
          }}
        >
          {pageCount !== null && (
            <Typography
              sx={{
                ...TEXT.meta,
                color: "text.secondary",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {pageCount}ページ
            </Typography>
          )}
          {isPurchased && <SalePurchaseChip fileCount={1} purchasedCount={1} />}
        </Box>
      )}
      {!isPurchased && priceUnset && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            color: "text.secondary",
          }}
        >
          <BlockOutlined aria-hidden sx={{ fontSize: "1rem" }} />
          <Typography sx={TEXT.meta}>現在購入できません</Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Box
      component="li"
      sx={(theme) => ({
        ...costItemSx(theme),
        flexWrap: "wrap",
        columnGap: 1,
        rowGap: 1,
        alignItems: "center",
        listStyle: "none",
        // チェックボックスの押せる範囲（44px）を行の左端に寄せる
        ...(reserveCheckboxSpace ? { pl: 0.5 } : {}),
        transition: "background-color 0.2s, border-color 0.2s",
        ...NO_TRANSITION,
        ...(selected
          ? {
              backgroundColor: rowHoverBg(theme),
              borderColor: theme.palette.primary.main,
            }
          : {}),
      })}
    >
      {isPurchased ? (
        <>
          {/* 購入済みの行はチェックを出さない。文字の左端を未購入の行とそろえる */}
          {reserveCheckboxSpace && (
            <Box aria-hidden sx={{ width: MIN_TAP_TARGET, flexShrink: 0 }} />
          )}
          {body}
          <Box sx={{ flex: "0 0 auto", ml: "auto" }}>
            <Button
              ref={downloadRef}
              size="small"
              variant="outlined"
              className="TapExpanded"
              startIcon={<DownloadOutlined />}
              disabled={busy}
              onClick={onDownload}
              sx={(theme) => ({
                ...reassistButtonSx("outlined", "small")(theme),
                minWidth: theme.spacing(16),
              })}
            >
              ダウンロード
            </Button>
          </Box>
        </>
      ) : (
        // 行の文字を押しても選べるよう、チェックボックスと文字を label でまとめる
        <Box
          component="label"
          htmlFor={checkboxId}
          sx={{
            display: "flex",
            alignItems: "center",
            columnGap: 1,
            flex: "1 1 auto",
            minWidth: 0,
            cursor: selectable && !busy ? "pointer" : "default",
          }}
        >
          <Checkbox
            id={checkboxId}
            checked={selected}
            disabled={busy || !selectable}
            onChange={onToggle}
            inputProps={{ "aria-label": `${typeLabel}を選ぶ` }}
            sx={checkboxSx}
          />
          {body}
        </Box>
      )}
    </Box>
  );
};
